import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

/** Zpřístupní vnořeným `MenuItem`ům funkci pro zavření menu po výběru položky. */
const MenuCloseContext = createContext<(() => void) | null>(null);

export interface DropdownMenuProps {
  /** The element that opens the menu — typically a `Button`. */
  trigger: ReactElement<HTMLAttributes<HTMLElement>>;
  children: ReactNode;
  /** Open the menu programmatically; defaults to internal state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'end';
  /** Otevřít dolů (default) nebo nahoru nad trigger (pro patičku/taskbar). */
  placement?: 'bottom' | 'top';
  /**
   * Vykreslit neviditelný backdrop přes celý viewport (zavře na klik mimo). Nutné, když jsou na
   * stránce iframy (legacy okna) — `document` mousedown listener neodchytí klik uvnitř iframu.
   */
  backdrop?: boolean;
  /** Distance between trigger and menu in pixels (defaults to 4). */
  offset?: number;
  className?: string;
}

interface PortalRect {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

export function DropdownMenu({
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  align = 'start',
  placement = 'bottom',
  backdrop = false,
  offset = 4,
  className,
}: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const menuId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<PortalRect | null>(null);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      if (!triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const left = align === 'end' ? r.right : r.left;
      // 'top' = menu roste NAHORU nad trigger (kotvíme spodek menu k hornímu okraji triggeru).
      setRect(
        placement === 'top'
          ? { bottom: window.innerHeight - r.top + offset, left, width: r.width }
          : { top: r.bottom + offset, left, width: r.width }
      );
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, offset, align, placement]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const triggerProps = {
    ref: triggerRef as unknown as React.Ref<HTMLElement>,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (event: React.MouseEvent) => {
      trigger.props.onClick?.(event as React.MouseEvent<HTMLElement>);
      if (!event.defaultPrevented) setOpen(!open);
    },
  };

  const triggerEl = isValidElement(trigger)
    ? cloneElement(trigger, triggerProps as Record<string, unknown>)
    : trigger;

  const style: CSSProperties | undefined =
    rect && typeof window !== 'undefined'
      ? {
          position: 'fixed',
          top: rect.top,
          bottom: rect.bottom,
          left: align === 'end' ? undefined : rect.left,
          right: align === 'end' ? window.innerWidth - rect.left : undefined,
          minWidth: Math.max(220, rect.width),
          // Nad okny frameworku (z-index 10 + stackZ*100, neomezeně roste) — viz PhoenixDesktop.
          // Sdílený token --phx-z-overlay (stejná vrstva jako shadcn Popover).
          zIndex: 'var(--phx-z-overlay)',
        }
      : undefined;

  return (
    <>
      {triggerEl}
      {open && rect && typeof document !== 'undefined'
        ? createPortal(
            <>
              {backdrop ? (
                <div
                  className="z-phx-overlay-backdrop fixed inset-0"
                  onMouseDown={() => setOpen(false)}
                />
              ) : null}
              <div
                ref={menuRef}
                role="menu"
                id={menuId}
                style={style}
                className={cn(
                  'phx-dropdown-menu rounded-phx-md border-phx-border flex flex-col gap-0 border',
                  'bg-phx-surface p-phx-1 shadow-phx-md text-phx-md text-phx-text',
                  className
                )}
              >
                <MenuCloseContext.Provider value={() => setOpen(false)}>
                  {children}
                </MenuCloseContext.Provider>
              </div>
            </>,
            document.body
          )
        : null}
    </>
  );
}

export interface MenuItemProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'children'> {
  icon?: PhxIconName;
  /** Vlastní uzel do ikon-slotu (má přednost před `icon`) — např. fallback „tečka" bez konkrétní ikony. */
  iconNode?: ReactNode;
  hint?: ReactNode;
  shortcut?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  /**
   * Zavřít menu po kliknutí na položku (default `true` — standardní chování dropdown menu).
   * `false` pro položky, které mají menu nechat otevřené (např. vícenásobný výběr).
   */
  closeOnSelect?: boolean;
  children: ReactNode;
}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(function MenuItem(
  {
    icon,
    iconNode,
    hint,
    shortcut,
    active,
    disabled,
    tone = 'default',
    closeOnSelect = true,
    className,
    children,
    onClick,
    ...rest
  },
  ref
) {
  const close = useContext(MenuCloseContext);
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    onClick?.(event);
    // Po výběru položky zavři menu (pokud si volající nevynutil opak nebo event nezrušil).
    if (closeOnSelect && !event.defaultPrevented) close?.();
  };

  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      disabled={disabled}
      data-active={active ? '' : undefined}
      className={cn(
        'phx-menu-item gap-phx-3 rounded-phx-sm px-phx-3 py-phx-2 flex items-center text-left',
        'min-h-phx-control cursor-pointer border-0 bg-transparent text-inherit',
        'hover:bg-phx-surface-2',
        active && 'bg-phx-primary-tint text-phx-primary-active',
        tone === 'danger' && 'text-phx-danger hover:bg-phx-danger-tint',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      onClick={handleClick}
      {...rest}
    >
      {iconNode ? (
        <span className="inline-flex h-[13px] w-[13px] shrink-0 items-center justify-center">
          {iconNode}
        </span>
      ) : icon ? (
        <Icon
          name={icon}
          size={13}
          className={cn('shrink-0', tone === 'danger' && 'text-phx-danger')}
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint ? <span className="text-phx-xs text-phx-text-muted">{hint}</span> : null}
      {shortcut ? (
        <span className="text-phx-xs text-phx-text-muted bg-phx-surface-2 rounded-phx-sm px-phx-2_5 py-[1px] font-mono">
          {shortcut}
        </span>
      ) : null}
    </button>
  );
});

export function MenuDivider() {
  return <div role="separator" className="phx-menu-divider my-phx-1 bg-phx-border h-px" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="phx-menu-label px-phx-3 pb-phx-1 pt-phx-2 text-phx-xs text-phx-text-muted font-semibold tracking-wider uppercase">
      {children}
    </div>
  );
}
